/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import { IconWrapper } from "../../customHooks/useIcon";
import { Config } from "../../utils/config";
import { enqueueSnackbar } from "notistack";

interface FileUploadProps {
  placeholder?: string;
  name?: string;
  isErrored?: boolean;
  errorMessage?: any;
  isTouched?: boolean;
  disabled?: boolean;
  accept?: string;
  maxSizeMB?: number;
  onChange: (fileUrl: string) => void;
  supportedFormats: string;
  clearNameAndUrl?: boolean;
}
const FileUpload: React.FC<FileUploadProps> = ({
  placeholder,
  onChange,
  name,
  isErrored,
  errorMessage,
  isTouched,
  disabled = false,
  clearNameAndUrl = false,
  supportedFormats = "jpg, jpeg, png",
  accept = "image/*",
  maxSizeMB = 5,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadToCloudinary = useCallback(
    async (file: File) => {
      // Check file size
      if (file.size > maxSizeMB * 1024 * 1024) {
        alert(`File size exceeds ${maxSizeMB}MB limit`);
        return;
      }

      setFileName(file.name);
      setIsFocused(true);
      setIsUploading(true);

      try {
        // Create form data for upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", Config.CLOUDINARY_UPLOAD_PRESET || "");

        // Upload to Cloudinary
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${Config.CLOUDINARY_CLOUD_NAME}/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (data.secure_url) {
          onChange(data.secure_url);
          setFileUrl(data.secure_url);
          enqueueSnackbar("Upload successful", { variant: "success" });
        } else {
          //   throw new Error("Upload failed");
          enqueueSnackbar("Upload failed", { variant: "error" });
          return;
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setFileName("");
      } finally {
        setIsUploading(false);
      }
    },
    [maxSizeMB, onChange]
  );
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    uploadToCloudinary(files[0]);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleBlur = () => {
    if (!fileName) {
      setIsFocused(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || isUploading) return;
      setIsDragging(true);
    },
    [disabled, isUploading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || isUploading) return;
      e.dataTransfer.dropEffect = "copy";
    },
    [disabled, isUploading]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || isUploading) return;

      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        uploadToCloudinary(files[0]);
      }
    },
    [disabled, isUploading, uploadToCloudinary]
  );
  useEffect(() => {
    if (clearNameAndUrl) {
      setFileName("");
      setFileUrl("");
    }
  }, [clearNameAndUrl]);
  return (
    <div
      className={`w-full flex flex-col items-start gap-0 ${
        isErrored && isTouched ? "mb-6" : ""
      }`}
    >
      <div
        className={`flex flex-col items-center justify-center border-2 ${
          isDragging
            ? "border-primary-blue border-dashed bg-blue-50"
            : isErrored && isTouched
            ? "border-red-500"
            : "border-primary-border"
        } rounded-lg w-full min-h-[6rem] p-4 transition-all duration-200 
        ${
          disabled
            ? "cursor-not-allowed opacity-70"
            : "cursor-pointer hover:border-gray-400"
        }`}
        onClick={handleClick}
        onBlur={handleBlur}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        tabIndex={0}
      >
        <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
          <label
            className={`absolute left-0 top-0 text-gray-400 text-sm transition-all duration-300 ease-in-out pointer-events-none ${
              isFocused || fileName
                ? "top-[-20px] left-2 bg-white !text-primary-blackMain px-2 py-1 -translate-y-2 font-semibold"
                : ""
            }`}
          >
            {placeholder}
          </label>

          <input
            type="file"
            ref={fileInputRef}
            name={name}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || isUploading}
            accept={accept}
          />

          {isUploading ? (
            <div className="flex flex-col items-center">
              <p className="text-sm text-gray-500">Uploading...</p>
              <div className="mt-2 w-32 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary-blue animate-pulse rounded-full"></div>
              </div>
            </div>
          ) : fileUrl ? (
            <img
              src={fileUrl}
              alt="Uploaded file"
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <>
              <IconWrapper
                icon={FiUploadCloud}
                className={`h-8 w-8 ${
                  isDragging ? "text-primary-blue" : "text-gray-400"
                }`}
              />
              <p className="text-sm text-gray-500">
                {isDragging
                  ? "Drop file here"
                  : "Click to upload or drag and drop"}
              </p>
              <p className="text-xs text-gray-400">
                {`Supported formats: ${supportedFormats}`}
              </p>
            </>
          )}
        </div>
      </div>

      {isErrored && isTouched && (
        <span className="text-xs text-red-500 mt-1">{errorMessage}</span>
      )}
    </div>
  );
};

export default FileUpload;
